import { useCallback, useState } from 'react';
import { Sparkles, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';

/** Single shared flag — dismissing at the login control or the sidebar toggle
 *  silences the hint everywhere. */
const HINT_DISMISSED_KEY = 'theme-hint-dismissed';

function readDismissed(): boolean {
  try {
    return localStorage.getItem(HINT_DISMISSED_KEY) === 'true';
  } catch {
    // Storage unavailable (private mode) — treat as dismissed so we never nag.
    return true;
  }
}

interface ThemeDiscoveryHintProps {
  children: React.ReactNode;
  side?: 'top' | 'bottom' | 'left' | 'right';
  align?: 'start' | 'center' | 'end';
}

/**
 * First-launch discovery hint for the theme switcher (86d3buh1u). Wraps a theme
 * control and, until dismissed, draws an attention-grabbing pulse ring around it
 * and auto-opens a one-time "themes are here" callout — this is the first time
 * theming is surfaced to users, so it's meant to be hard to miss.
 *
 * Built on the shared Popover primitive in *anchor* mode (not trigger mode) so
 * the wrapped control keeps its own click behaviour. Dismissal is instant
 * (Performance gate) and persisted to one shared localStorage flag, so it
 * doesn't nag on repeat visits or across the login/sidebar controls.
 */
export function ThemeDiscoveryHint({
  children,
  side = 'bottom',
  align = 'end',
}: ThemeDiscoveryHintProps) {
  const [dismissed, setDismissed] = useState(readDismissed);
  // Controlled so dismissing closes the callout in the same paint as the click.
  const [open, setOpen] = useState(() => !readDismissed());

  const dismiss = useCallback(() => {
    setOpen(false);
    setDismissed(true);
    try {
      localStorage.setItem(HINT_DISMISSED_KEY, 'true');
    } catch {
      /* persistence best-effort — the in-memory dismissal still applies */
    }
  }, []);

  return (
    <Popover open={open} onOpenChange={(next) => !next && dismiss()}>
      <PopoverAnchor asChild>
        <div className="relative inline-flex">
          {!dismissed && (
            <>
              {/* Radar pulse — expands + fades to draw the eye to the control. */}
              <span
                aria-hidden
                className="pointer-events-none absolute inset-0 animate-ping rounded-md ring-2 ring-primary/60"
              />
              <span
                aria-hidden
                className="pointer-events-none absolute inset-0 rounded-md ring-2 ring-primary/50"
              />
            </>
          )}
          {children}
        </div>
      </PopoverAnchor>
      <PopoverContent
        side={side}
        align={align}
        className="w-64"
        // Don't steal focus on auto-open — the login form / page stays usable.
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <button
          type="button"
          aria-label="Dismiss"
          onClick={dismiss}
          className="absolute right-2 top-2 text-muted-foreground transition-colors hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="flex items-start gap-3 pr-4">
          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Sparkles className="h-4 w-4" />
          </span>
          <div className="space-y-1">
            <p className="text-sm font-semibold leading-none">New: themes are here</p>
            <p className="text-sm text-muted-foreground">
              Switch between Light, Dark, and Industrial looks — right here, anytime.
            </p>
            <Button size="sm" className="mt-2 h-7" onClick={dismiss}>
              Got it
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
